<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import useMpm from './useMpm'
import dayjs from 'dayjs'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Autoplay, A11y } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/autoplay'
import { getTvbPlayerType } from '@/utils'

const props = defineProps({
  size: { type: String, default: null },
  mpms: { type: Array, default: () => [] }
})

const { mpmImpression, mpmTracking, getVisible } = useMpm()
const isMobile = computed(() => ['s', 'xs'].includes(props.size))
const toDateTime = (time) => {
  if (time && dayjs(time).isValid()) {
    return dayjs.unix(time).format('YYYY-MM-DD HH:mm')
  } else {
    return ''
  }
}

const tvbPlayerRef = ref()
const mpmRef = ref()
const realIndex = ref(-1)
const onActiveIndexChange = (e) => {
  if (!e?.autoplay?.running) mpmRef.value?.$el?.swiper?.autoplay?.start()
  realIndex.value = e.realIndex
}

// 监听播放
const onStateChange = ({ index, state }) => {
  if (index === realIndex.value) {
    if (state === 'play' || state === 'loading') {
      mpmRef.value?.$el?.swiper?.autoplay?.stop()
    } else {
      mpmRef.value?.$el?.swiper?.autoplay?.start()
    }
  }
}

// 处理自动播放
const onSlideChangeTransitionEnd = () => {
  nextTick(async () => {
    if (Array.isArray(tvbPlayerRef.value)) {
      for (const element of tvbPlayerRef.value) {
        let visible = await getVisible(element)
        if (visible) {
          setTimeout(() => element?.play?.(), 300)
          break
        }
      }
    }
  })
}

// 初始化后
const onAfterInit = () => onSlideChangeTransitionEnd()

watch(realIndex, (value) => {
  if (Array.isArray(tvbPlayerRef.value)) {
    tvbPlayerRef.value.forEach((element) => element?.pause?.())
  }
  const res = props.mpms?.[value]
  mpmImpression(res)
})

const onMouseover = (index) => {
  if (isMobile.value) return
  mpmRef.value.$el.swiper.slideToLoop(index)
}
</script>
<template>
  <div
    class="tvb-main-category-mpm"
    :class="[
      size ? 'mpm-' + size : '',
      { 'slide_image-hover': !isMobile },
      { 'mpm-onlyone': mpms.length === 1 }
    ]"
  >
    <swiper
      ref="mpmRef"
      class="tvb-main-category-swiper"
      :class="{ 'tvb-hairline--surround': !isMobile }"
      :modules="[Autoplay, A11y]"
      :loop="mpms.length > 1"
      :autoplay="{ delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true }"
      @afterInit="onAfterInit"
      @activeIndexChange="onActiveIndexChange"
      @slideChangeTransitionEnd="onSlideChangeTransitionEnd"
    >
      <swiper-slide
        v-for="(item, index) in mpms"
        :key="index"
        class="tvb-main-category-swiper_slide"
      >
        <tvb-router-link
          :to="item.link"
          :linkRel="item.link_rel"
          :linkTarget="item.link_target"
          @click="mpmTracking(item, item.video_url ? 'video' : 'image')"
        >
          <tvb-image
            class="tvb-main-category-swiper_slide_image"
            :src="item.image_info"
            :alt="item.title"
            :transform="isMobile ? false : true"
            backgroundc="#F5F6F7"
          />
          <tvb-player
            v-if="item.video_url"
            :id="item.id"
            ref="tvbPlayerRef"
            class="tvb-main-category_swiper-player"
            :src="item.video_url"
            :type="getTvbPlayerType(item?.video_type)"
            :content-id="item?.content_id"
            :region-only="item?.region_only"
            :poster-url="item?.image_info?.large"
            :title="item?.title"
            :youbora-options="{
              videoTitle: item?.title,
              programTitle: item?.program_title,
              videoSrc: item?.video_url,
              contentId: item?.video_id,
              articleId: item?.id === item?.video_id ? '' : item?.id
            }"
            :mute="true"
            :playerVars="{ controls: 0 }"
            @state-change="(player) => onStateChange({ ...player, index })"
          ></tvb-player>
          <div class="tvb-main-category_swiper-player-mask"></div>
        </tvb-router-link>
        <div class="tvb-main-category-swiper_slide_mask">
          <tvb-router-link
            :to="item.link"
            :linkRel="item.link_rel"
            :linkTarget="item.link_target"
            @click="mpmTracking(item, 'text')"
          >
            <h3 class="clear-h tvb-swiper_slide_mask-tit" :title="item.title">
              {{ item.title }}
            </h3>
            <div v-if="!isMobile" class="tvb-swiper_slide_mask-owner">
              <span class="tvb-swiper_slide_mask-author" :title="item.category_name">
                {{ item.category_name }}
              </span>
              <span v-if="item.publish_at" class="tvb-swiper_slide_mask-date">
                {{ toDateTime(item.publish_at) }}
              </span>
            </div>
          </tvb-router-link>
        </div>
      </swiper-slide>

      <template v-slot:container-end>
        <template v-if="mpms.length > 1">
          <div
            class="tvb-main-category-mpm_nav mpm_nav-prev"
            @click.stop="() => mpmRef.$el.swiper.slidePrev()"
          >
            <tvb-svg-icon name="icon_arrow_mpm" />
          </div>
          <div
            class="tvb-main-category-mpm_nav mpm_nav-next"
            @click.stop="() => mpmRef.$el.swiper.slideNext()"
          >
            <tvb-svg-icon name="icon_arrow_mpm" />
          </div>
        </template>
      </template>
    </swiper>

    <div class="tvb-main-category-mpm_page">
      <div class="tvb-main-category-mpm_page_wrap">
        <div
          class="tvb-main-category-mpm_dot"
          v-for="(item, index) in mpms"
          :key="index"
          :class="[{ active: realIndex === index }, { hover: !isMobile }]"
          @click="onMouseover(index)"
        ></div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tvb-main-category-mpm {
  position: relative;
  width: 100%;
  height: 100%;

  .tvb-main-category-swiper {
    width: 100%;
    height: 100%;
    border-radius: 20px;
    --tvb-hairline-border-radius: calc(2 * 20px);
  }

  // hover
  &.slide_image-hover {
    &:hover .tvb-main-category-mpm_nav {
      &.mpm_nav-prev {
        left: 0;
      }
      &.mpm_nav-next {
        right: 0;
      }
    }
  }

  // 导航按钮
  .tvb-main-category-mpm_nav {
    position: absolute;
    top: calc(200 / 477 * 100%);
    width: 28px;
    height: 48px;
    background-color: rgba(0, 0, 0, 0.6);
    z-index: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 18px;
    border-radius: 8px 0px 0px 8px;
    user-select: none;
    cursor: pointer;
    transition:
      left 0.15s linear,
      right 0.15s linear,
      background-color 0.15s linear;
    &.mpm_nav-prev {
      left: -28px;
      transform: scaleX(-1);
      transition:
        left 0.15s linear,
        right 0.15s linear,
        background-color 0.15s linear;
    }
    &.mpm_nav-next {
      transition:
        left 0.15s linear,
        right 0.15s linear,
        background-color 0.15s linear;
      right: -28px;
    }
    .svg-icon {
      opacity: 0.85;
      transition: opacity 0.15s linear;
    }

    &.mpm_nav-prev:hover,
    &.mpm_nav-next:hover {
      background-color: rgba(0, 0, 0, 0.8);
      .svg-icon {
        opacity: 1;
      }
    }
  }

  .tvb-main-category-swiper_slide {
    a {
      text-decoration: none;
      background-color: transparent;
      color: inherit;
      cursor: pointer;
      &:visited {
        color: inherit;
      }
    }

    .tvb-main-category_swiper-player {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      :deep(.tvb-player-play) {
        top: calc(200 / 477 * 100% - 48px);
      }
      &::after {
        content: ' ';
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
      }
    }
    .tvb-main-category_swiper-player-mask {
      width: 100%;
      height: 100%;
      position: absolute;
      left: 0;
      top: 0;
      z-index: 2;
    }

    .tvb-main-category-swiper_slide_mask {
      width: 100%;
      position: absolute;
      left: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.6);
      padding: 16px 16px 36px 16px;
      line-height: 0;

      .tvb-swiper_slide_mask-tit {
        @include line-text-ellipsis(1);
        color: rgba(255, 255, 255, 0.95);
        font-size: 22px;
        font-weight: 500;
        letter-spacing: 0.5px;
        line-height: 32px;
        // text-shadow: 0px 0px 4px rgba(0, 0, 0, 0.6);
      }

      .tvb-swiper_slide_mask-owner {
        display: flex;
        align-items: center;
        display: inline-flex;
        color: rgba(255, 255, 255, 0.8);
        font-size: 14px;
        letter-spacing: 0;
        line-height: 20px;
        letter-spacing: 0.3px;
        font-weight: 400;
      }

      .tvb-swiper_slide_mask-author {
        flex: 1;
        // text-shadow: 0px 0px 4px rgba(0, 0, 0, 0.6);
        @include line-text-ellipsis(1);
      }

      .tvb-swiper_slide_mask-date {
        // text-shadow: 0px 0px 4px rgba(0, 0, 0, 0.6);
        margin-left: 12px;
      }
    }
  }

  &.mpm-s,
  &.mpm-xs {
    padding-bottom: 9px;
    --tvb-mpm-s-border-radius: 16px;
    .tvb-main-category-swiper {
      border-radius: 16px;
      border-radius: var(--tvb-mpm-s-border-radius);
    }

    .tvb-main-category_swiper-player {
      :deep(.tvb-player-play) {
        top: calc(((100% - 40px) / 2) - 40px / 2); // (100% - mask高度) - icon大小
      }
    }

    .tvb-main-category-swiper_slide_mask {
      padding: 16px 12px 8px 12px;
      border-radius: var(--tvb-mpm-s-border-radius);
      background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0) 0%,
        rgba(0, 0, 0, 0.4) 50%,
        rgba(0, 0, 0, 0.6) 100%
      );
      .tvb-swiper_slide_mask-tit {
        font-size: 16px;
        line-height: 24px;
        text-shadow: 0 0 4px rgba(0, 0, 0, 0.6);
      }
    }
  }

  // 分页器
  .tvb-main-category-mpm_page {
    position: absolute;
    left: 16px;
    bottom: 16px;
    z-index: 1;
    user-select: none;

    .tvb-main-category-mpm_page_wrap {
      display: flex;

      .tvb-main-category-mpm_dot {
        height: 8px;
        width: 8px;
        background-color: rgba(255, 255, 255, 0.3);
        border-radius: 50%;
        transition: background-color 0.15s linear;
        cursor: pointer;

        & + .tvb-main-category-mpm_dot {
          margin-left: 8px;
        }

        &.active {
          background-color: rgba(255, 255, 255, 0.9);
        }
        &.hover:hover {
          background-color: rgba(255, 255, 255, 0.9);
        }
      }
    }
  }

  &.mpm-s,
  &.mpm-xs {
    &.mpm-onlyone {
      .tvb-main-category-mpm_page {
        display: none;
      }
    }

    .tvb-main-category-mpm_page {
      left: 50%;
      bottom: 0;
      transform: translateX(-50%);
      .tvb-main-category-mpm_dot {
        height: 3px;
        background-color: rgba(163, 178, 188, 0.35);
        border-radius: 2px;
        cursor: auto;
        & + .tvb-main-category-mpm_dot {
          margin-left: 4px;
        }
        &.active {
          background-color: #818d95;
          background:
            linear-gradient(0deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.2) 100%), #231f20;
        }
      }
    }
  }
}
</style>
